import type { PropsWithChildren } from 'react'
import { createContext, useContext, useState, useMemo, useCallback } from 'react'

import { EditAttendanceModal } from './editAttendance/EditAttendanceModal'
import { CancelOpportunityModal } from './cancelOpportunity/CancelOpportunityModal'
import { CancelOpportunitySuccessModal } from './cancelOpportunity/CancelOpportunitySuccessModal'
import { CancelOpportunityFailureModal } from './cancelOpportunity/CancelOpportunityFailureModal'

type Opportunity = { id: string; name: string }
type Err = Record<'code' | 'message' | 'title', string>

export type VolunteeringActionsDialogsContextType = Readonly<{
  editAttendance: (opportunity: Opportunity) => void
  cancelOpportunity: (opportunity: Opportunity) => void
}>

const VolunteeringActionsDialogsContext = createContext<VolunteeringActionsDialogsContextType | null>(null)

export type VolunteeringActionsDialogsProviderProps = Readonly<{
  refresh?: () => void
}>

export function VolunteeringActionsDialogsProvider({
  children,
  refresh
}: PropsWithChildren<VolunteeringActionsDialogsProviderProps>) {
  const [editingAttendance, setEditingAttendance] = useState<Opportunity | null>(null)

  const [cancellingOpportunity, setCancelOpportunity] = useState<Opportunity | null>(null)
  const [cancellingOpportunitySuccess, setCancelOpportunitySuccess] = useState<boolean>(false)
  const [cancellingOpportunityError, setCancelOpportunityError] = useState<Err | null>(null)

  const editAttendance = useCallback((opportunity: Opportunity) => setEditingAttendance(opportunity), [])
  const closeEditAttendanceDialog = useCallback(() => setEditingAttendance(null), [])

  const onEditAttendanceSuccess = useCallback(() => {
    setEditingAttendance(null)
  }, [])

  const cancelOpportunity = useCallback((opportunity: Opportunity) => setCancelOpportunity(opportunity), [])
  const closeCancelOpportunityDialog = useCallback(() => setCancelOpportunity(null), [])

  const onCancelOpportunitySuccess = useCallback(() => {
    setCancelOpportunity(null)
    setCancelOpportunitySuccess(true)
    refresh?.()
  }, [refresh])

  const onCancelOpportunityError = useCallback((error?: Err) => {
    setCancelOpportunity(null)
    setCancelOpportunityError(error ?? null)
  }, [])

  const value = useMemo(
    () => ({
      editAttendance,
      cancelOpportunity
    }),
    [editAttendance, cancelOpportunity]
  )

  return (
    <VolunteeringActionsDialogsContext.Provider value={value}>
      {children}
      <EditAttendanceModal
        isOpen={!!editingAttendance}
        onClose={closeEditAttendanceDialog}
        opportunityName={editingAttendance?.name ?? ''}
        onSuccess={onEditAttendanceSuccess}
      />
      <CancelOpportunityModal
        isOpen={!!cancellingOpportunity}
        onClose={closeCancelOpportunityDialog}
        opportunityId={cancellingOpportunity?.id ?? ''}
        opportunityName={cancellingOpportunity?.name ?? ''}
        onSuccess={onCancelOpportunitySuccess}
        onError={onCancelOpportunityError}
      />
      <CancelOpportunitySuccessModal
        isOpen={cancellingOpportunitySuccess}
        onClose={() => setCancelOpportunitySuccess(false)}
        opportunityName={cancellingOpportunity?.name ?? ''}
      />
      <CancelOpportunityFailureModal
        isOpen={!!cancellingOpportunityError}
        handleClose={() => setCancelOpportunityError(null)}
      />
    </VolunteeringActionsDialogsContext.Provider>
  )
}

export const useVolunteeringActionsDialogs = () => {
  const context = useContext(VolunteeringActionsDialogsContext)

  if (!context) {
    throw new Error('useVolunteeringActionsDialogs must be used within a VolunteeringActionsDialogsProvider')
  }

  return context
}
